import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../components/Header';
import { useToast } from '../components/Toast';
import { getObjects, updateObject, deleteObject } from '../api/client';

const API = '';
const TYPES = ['Все', 'Малярка', 'Электрика', 'Сантехника', 'Плитка', 'Фасады', 'Кровля', 'Гипсокартон', 'Полы', 'Демонтаж', 'Наружные работы', 'Под ключ', 'Другое'];

// Moldova cities
const CITY_PLZ = {
  'chisinau': 'MD-2000', 'кишинёв': 'MD-2000', 'кишинев': 'MD-2000',
  'balti': 'MD-3100', 'бэлць': 'MD-3100', 'бельцы': 'MD-3100',
  'cahul': 'MD-3900', 'кахул': 'MD-3900',
  'comrat': 'MD-3800', 'комрат': 'MD-3800',
  'tiraspol': 'MD-3300', 'тирасполь': 'MD-3300',
  'orhei': 'MD-3500', 'орхей': 'MD-3500',
  'ungheni': 'MD-3600', 'унгень': 'MD-3600',
  'soroca': 'MD-4500', 'сорока': 'MD-4500',
  'edinet': 'MD-4600', 'единец': 'MD-4600',
  'causeni': 'MD-4300', 'каушаны': 'MD-4300',
  'drochia': 'MD-5200', 'дрокия': 'MD-5200',
  'straseni': 'MD-3700', 'стрэшень': 'MD-3700',
  'floresti': 'MD-5000', 'флорешть': 'MD-5000',
  'criuleni': 'MD-4800', 'криулень': 'MD-4800',
  'dubasari': 'MD-4500', 'дубоссары': 'MD-4500',
  'hincesti': 'MD-3400', 'хынчешть': 'MD-3400',
  'ialoveni': 'MD-6800', 'яловень': 'MD-6800',
  'stefan voda': 'MD-4200', 'штефан водэ': 'MD-4200',
  'rezina': 'MD-5400', 'резина': 'MD-5400',
  'taraclia': 'MD-7400', 'тараклия': 'MD-7400',
};

// Display-name list for autocomplete (capitalize first letter of each word)
const CITY_NAMES = Object.keys(CITY_PLZ).map(k => k.replace(/\b\w/g, c => c.toUpperCase()));

function findPlz(city) {
  if (!city) return '';
  const key = city.trim().toLowerCase();
  return CITY_PLZ[key] || '';
}

// SQLite stores timestamps as "YYYY-MM-DD HH:MM:SS" (UTC). Safari/iOS (Telegram
// on iPhone) can't parse the space form and yields "Invalid Date" — normalise to ISO.
function formatDate(s) {
  if (!s) return '';
  const str = String(s);
  const iso = str.replace(' ', 'T') + (/[zZ]|[+-]\d\d:?\d\d$/.test(str) ? '' : 'Z');
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' });
}

function suggestCities(input) {
  if (!input || input.length < 2) return [];
  const q = input.trim().toLowerCase();
  return CITY_NAMES.filter(c => c.toLowerCase().startsWith(q)).slice(0, 5);
}

const statusBadge = (obj) => {
  if (obj.object_type === 'recommendation')
    return { text: 'Рекомендация', bg: 'rgba(10,132,255,0.12)', color: '#0A84FF' };
  if (obj.urgency === 'urgent' && obj.status === 'active')
    return { text: 'Срочный', bg: 'rgba(255,159,10,0.12)', color: '#FF9F0A' };
  if (obj.status === 'active')
    return { text: 'Активный', bg: 'rgba(48,209,88,0.12)', color: 'var(--success)' };
  return { text: 'Закрыт', bg: 'rgba(99,99,102,0.12)', color: 'var(--text-tertiary)' };
};

const inputStyle = {
  width: '100%', background: 'var(--bg-primary)', border: '1px solid var(--border)',
  borderRadius: 10, padding: '10px 12px', fontSize: 15, color: 'var(--text-primary)',
  fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};

const isPhone = (str) => /^\+?\d[\d\s\-()]{6,}$/.test(str?.trim());
const isTelegram = (str) => /^@/.test(str?.trim());

// Parse recommendation fields from description
function parseRecDescription(desc) {
  if (!desc) return { telegram: '', comment: '' };
  const lines = desc.split('\n').filter(Boolean);
  let telegram = '';
  const commentLines = [];
  lines.forEach(l => {
    if (l.trim().startsWith('@')) telegram = l.trim();
    else commentLines.push(l);
  });
  return { telegram, comment: commentLines.join('\n') };
}

export default function ObjectsPage({ onNavigate }) {
  const { toast, toastError } = useToast();
  const [objects, setObjects] = useState([]);
  const [counts, setCounts] = useState({ active: 0, closed: 0 });
  const [filter, setFilter] = useState('Все');
  const [showForm, setShowForm] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showRecForm, setShowRecForm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [dragY, setDragY] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [editingObj, setEditingObj] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileRef = useRef(null);
  const touchStartY = useRef(null);
  const touchStartX = useRef(null);
  const dragAxis = useRef(null); // 'x' | 'y' once the gesture locks to an axis
  const startScrollTop = useRef(0);
  const modalRef = useRef(null);
  const [customType, setCustomType] = useState('');
  const [budgetMode, setBudgetMode] = useState('amount'); // amount, negotiable, discuss
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { photos, index }
  const lbTouchX = useRef(0);
  const [form, setForm] = useState({ types: [], city: '', district: '', title: '', description: '', budget: '', workers_needed: 1, urgency: 'normal', start_date: '', start_date_exact: '', contact: '' });
  const [recForm, setRecForm] = useState({ types: [], city: '', contactName: '', phone: '', telegram: '', comment: '' });
  const [recCitySuggestions, setRecCitySuggestions] = useState([]);

  const load = useCallback(() => {
    const params = filter !== 'Все' ? `type=${encodeURIComponent(filter)}` : '';
    getObjects(params).then(data => {
      setObjects(data.objects || []);
      setCounts(data.counts || { active: 0, closed: 0 });
    }).catch(console.error);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Clean up dropdowns when returning from file picker / app regains focus
  useEffect(() => {
    const handleFocus = () => { setCitySuggestions([]); setRecCitySuggestions([]); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const FORM_TYPES = TYPES.filter(t => t !== 'Все');

  const toggleType = (arr, t) => arr.includes(t) ? arr.filter(x => x !== t) : [...arr, t];

  const resetForm = () => {
    setForm({ types: [], city: '', district: '', title: '', description: '', budget: '', workers_needed: 1, urgency: 'normal', start_date: '', start_date_exact: '', contact: '' });
    setCustomType('');
    setBudgetMode('amount');
    setEditingObj(null);
    setPhotos([]);
    setPreviews([]);
  };

  const resetRecForm = () => {
    setRecForm({ types: [], city: '', contactName: '', phone: '', telegram: '', comment: '' });
    setFormErrors({});
  };

  const openCreate = () => { setShowChoiceModal(true); };

  const openEdit = (obj) => {
    const parsedTypes = (obj.type || '').split(',').map(s => s.trim()).filter(Boolean);
    const knownTypes = parsedTypes.filter(t => FORM_TYPES.includes(t));
    const unknownTypes = parsedTypes.filter(t => !FORM_TYPES.includes(t) && t !== 'Другое');
    const editTypes = unknownTypes.length > 0 ? [...knownTypes, 'Другое'] : knownTypes;
    if (unknownTypes.length > 0) setCustomType(unknownTypes.join(', '));
    setForm({
      types: editTypes.length > 0 ? editTypes : ['Малярка'], city: obj.city || '', title: obj.title || '',
      description: obj.description || '', budget: obj.budget || '',
      workers_needed: obj.workers_needed || 1, urgency: obj.urgency || 'normal',
      start_date: obj.start_date || '', start_date_exact: '', contact: obj.contact || '',
    });
    setEditingObj(obj);
    setShowDetail(null);
    setPhotos([]);
    setPreviews([]);
    setShowForm(true);
  };

  const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB

  const handlePhotos = (e) => {
    // Close any open dropdowns after returning from file picker
    setCitySuggestions([]);
    setRecCitySuggestions([]);
    const files = Array.from(e.target.files).slice(0, 10 - photos.length);
    if (files.length === 0) return;
    const tooBig = files.filter(f => f.size > MAX_PHOTO_SIZE);
    if (tooBig.length > 0) {
      toastError(`Файл "${tooBig[0].name}" слишком большой (макс. 5 МБ)`);
      e.target.value = '';
      return;
    }
    const valid = files.filter(f => f.size <= MAX_PHOTO_SIZE);
    setPhotos(prev => [...prev, ...valid]);
    valid.forEach(f => {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(f);
    });
    e.target.value = '';
    // iOS fix: blur file input and force layout recalc after native picker closes
    e.target.blur();
    setTimeout(() => { window.dispatchEvent(new Event('resize')); }, 100);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const errors = {};
    if (form.types.length === 0) errors.types = true;
    if (!form.city.trim()) errors.city = true;
    if (!form.title.trim()) errors.title = true;
    if (!form.contact.trim()) errors.contact = true;
    if (form.types.includes('Другое') && !customType.trim()) errors.customType = true;
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Scroll to first error field
      setTimeout(() => {
        const el = document.querySelector('[data-error="true"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setSubmitting(true);
    // Build type string from selected types
    const typesList = form.types.map(t => t === 'Другое' ? customType.trim() : t).filter(Boolean);
    const submitForm = { ...form, type: typesList.join(', '), workers_needed: parseInt(form.workers_needed) || 1 };
    delete submitForm.types;
    if (budgetMode === 'negotiable') { submitForm.budget = -1; submitForm.budget_label = 'Договорная'; }
    else if (budgetMode === 'discuss') { submitForm.budget = -2; submitForm.budget_label = 'Обсуждается'; }
    if (submitForm.start_date === 'Точная дата' && submitForm.start_date_exact) {
      submitForm.start_date = new Date(submitForm.start_date_exact).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    delete submitForm.start_date_exact;
    try {
      if (editingObj) {
        const data = { ...submitForm, budget: submitForm.budget ? parseFloat(submitForm.budget) : null };
        await updateObject(editingObj.id, data);
      } else {
        const formData = new FormData();
        Object.keys(submitForm).forEach(k => { if (submitForm[k] !== '' && submitForm[k] !== null && submitForm[k] !== undefined) formData.append(k, submitForm[k]); });
        formData.append('object_type', 'object');
        photos.forEach(f => formData.append('photos', f));
        const initData = window.Telegram?.WebApp?.initData;
        const headers = {};
        if (initData) headers['X-Telegram-InitData'] = initData;
        else headers['X-Dev-User'] = '100001';
        const res = await fetch(API + '/api/objects', { method: 'POST', headers, body: formData });
        if (!res.ok) throw new Error('Ошибка сервера');
      }
      toast(editingObj ? 'Объект обновлён' : 'Объект создан');
      setShowForm(false);
      resetForm();
      setTimeout(load, 300);
    } catch (e) {
      toastError(e.message || 'Не удалось сохранить объект');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecSubmit = async () => {
    if (submitting) return;
    const errors = {};
    if (recForm.types.length === 0) errors.recType = true;
    if (!recForm.city.trim()) errors.recCity = true;
    if (!recForm.contactName.trim()) errors.recName = true;
    if (!recForm.phone.trim()) errors.recPhone = true;
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    const descParts = [];
    if (recForm.telegram.trim()) descParts.push(recForm.telegram.trim());
    if (recForm.comment.trim()) descParts.push(recForm.comment.trim());

    const formData = new FormData();
    formData.append('type', recForm.types.join(', '));
    formData.append('city', recForm.city);
    formData.append('title', `Рекомендация: ${recForm.contactName.trim()}`);
    formData.append('contact', recForm.phone.trim());
    formData.append('description', descParts.join('\n'));
    formData.append('object_type', 'recommendation');

    try {
      const initData = window.Telegram?.WebApp?.initData;
      const headers = {};
      if (initData) headers['X-Telegram-InitData'] = initData;
      else headers['X-Dev-User'] = '100001';
      const res = await fetch(API + '/api/objects', { method: 'POST', headers, body: formData });
      if (!res.ok) throw new Error('Ошибка сервера');
      toast('Рекомендация отправлена');
      setShowRecForm(false);
      resetRecForm();
      setTimeout(load, 300);
    } catch (e) {
      toastError(e.message || 'Не удалось отправить рекомендацию');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (obj) => {
    try {
      const newStatus = obj.status === 'active' ? 'closed' : 'active';
      await updateObject(obj.id, { status: newStatus });
      toast(newStatus === 'closed' ? 'Объект закрыт' : 'Объект открыт');
      setShowDetail(null);
      load();
    } catch (e) {
      toastError('Не удалось изменить статус');
    }
  };

  // Swipe down to close modal — only on a deliberate single-finger drag that
  // begins at the very top of the modal. Guards against stale refs / multi-touch
  // so the sheet doesn't close or jitter right after it opens (iOS).
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) { touchStartY.current = null; return; }
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
    startScrollTop.current = modalRef.current?.scrollTop ?? 0;
    dragAxis.current = null;
  };
  // Drag the sheet with the finger: down (from the top of the scroll) or sideways.
  const handleTouchMove = (e) => {
    if (touchStartY.current == null) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    const dx = e.touches[0].clientX - touchStartX.current;
    // Lock to the dominant axis once the finger has moved a little.
    if (!dragAxis.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      dragAxis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (dragAxis.current === 'y') {
      if (dy > 0 && startScrollTop.current <= 0) {
        if (!dragging) setDragging(true);
        setDragY(dy);
      }
    } else {
      // Only follow rightward drags (swipe right to close).
      if (!dragging) setDragging(true);
      setDragX(Math.max(0, dx));
    }
  };
  const handleTouchEnd = (e) => {
    if (touchStartY.current == null) return; // no valid gesture started on this modal
    const axis = dragAxis.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartY.current = null; // consume the gesture
    dragAxis.current = null;
    setDragging(false);
    if (axis === 'y' && dy > 110 && startScrollTop.current <= 0) {
      // Animate the sheet down off-screen, then unmount.
      setDragY(window.innerHeight);
      setTimeout(() => { setShowDetail(null); setDragY(0); }, 300);
    } else if (axis === 'x' && dx > 110) {
      // Animate the sheet off to the right, then unmount.
      setDragX(window.innerWidth);
      setTimeout(() => { setShowDetail(null); setDragX(0); }, 300);
    } else {
      setDragY(0); // snap back
      setDragX(0);
    }
  };

  const ContactLink = ({ value }) => {
    if (!value) return null;
    const v = value.trim();
    if (isPhone(v)) return <a href={`tel:${v.replace(/[\s\-()]/g, '')}`} style={{ color: '#0A84FF', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>📞 {v}</a>;
    if (isTelegram(v)) return <a href={`https://t.me/${v.slice(1)}`} target="_blank" rel="noreferrer" style={{ color: '#0A84FF', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>💬 {v}</a>;
    return <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{v}</span>;
  };

  // Render recommendation card (compact)
  const renderRecCard = (obj, i) => {
    const badge = statusBadge(obj);
    const contactName = (obj.title || '').replace(/^Рекомендация:\s*/, '');
    const rec = parseRecDescription(obj.description);
    return (
      <div key={obj.id} onClick={() => setShowDetail(obj)} style={{
        background: 'var(--bg-card)', borderRadius: 16, overflow: 'hidden',
        border: '1px solid var(--border)', opacity: obj.status === 'closed' ? 0.5 : 1,
        animation: `fadeInUp 0.3s ease ${0.05 * i}s both`, cursor: 'pointer',
      }}>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: badge.bg, color: badge.color }}>{badge.text}</span>
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{obj.type}</span>
            </div>
            <span style={{ fontSize: 12, color: '#0A84FF' }}>📍 {obj.city}</span>
          </div>

          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{contactName}</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: rec.comment ? 8 : 0 }}>
            <a href={`tel:${obj.contact?.replace(/[\s\-()]/g, '')}`} onClick={e => e.stopPropagation()} style={{ color: '#0A84FF', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>📞 {obj.contact}</a>
            {rec.telegram && (
              <a href={`https://t.me/${rec.telegram.replace('@', '')}`} onClick={e => e.stopPropagation()} target="_blank" rel="noreferrer" style={{ color: '#0A84FF', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>💬 {rec.telegram}</a>
            )}
          </div>

          {rec.comment && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {rec.comment}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
                {obj.author_initials || '??'}
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{obj.author_name || 'Аноним'}</span>
            </div>
            {obj.is_author && (
              <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => handleToggleStatus(obj)} style={{ background: 'var(--bg-elevated)', color: obj.status === 'active' ? 'var(--success)' : '#0A84FF', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  {obj.status === 'active' ? '✓' : '↺'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render regular object card
  const renderObjectCard = (obj, i) => {
    const badge = statusBadge(obj);
    const thumb = obj.photos?.[0];
    return (
      <div key={obj.id} onClick={() => setShowDetail(obj)} style={{
        background: 'var(--bg-card)', borderRadius: 16, overflow: 'hidden',
        border: '1px solid var(--border)', opacity: obj.status === 'closed' ? 0.5 : 1,
        animation: `fadeInUp 0.3s ease ${0.05 * i}s both`, cursor: 'pointer',
      }}>
        {/* Photo thumbnail */}
        {thumb && (
          <div style={{ width: '100%', maxHeight: 200, overflow: 'hidden', position: 'relative', background: '#111' }}>
            <img src={thumb} alt="" style={{ width: '100%', display: 'block' }} />
            {obj.photos.length > 1 && (
              <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, color: '#fff' }}>
                📷 {obj.photos.length}
              </div>
            )}
          </div>
        )}

        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: badge.bg, color: badge.color }}>{badge.text}</span>
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{obj.type}</span>
            </div>
            <span onClick={e => { e.stopPropagation(); window.open(`https://www.google.com/maps/search/${encodeURIComponent(obj.city + (obj.district ? ' ' + obj.district : '') + ', Moldova')}`); }} style={{ fontSize: 12, color: '#0A84FF', cursor: 'pointer' }}>📍 {obj.city}{obj.district ? ` ${obj.district}` : ''}</span>
          </div>

          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, lineHeight: 1.3 }}>{obj.title}</div>

          {obj.description && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {obj.description}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
              {obj.start_date && <span>🗓 {obj.start_date}</span>}
              {obj.workers_needed > 1 && <span>👷 {obj.workers_needed} чел</span>}
            </div>
            {(obj.budget > 0 || obj.budget_label) && <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)' }}>{obj.budget_label || `${obj.budget.toLocaleString()} MDL`}</span>}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
                {obj.author_initials || '??'}
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{obj.author_name || 'Аноним'}</span>
            </div>
            {obj.is_author && (
              <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => openEdit(obj)} style={{ background: 'var(--bg-elevated)', color: 'var(--accent)', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>✎</button>
                <button onClick={() => handleToggleStatus(obj)} style={{ background: 'var(--bg-elevated)', color: obj.status === 'active' ? 'var(--success)' : '#0A84FF', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  {obj.status === 'active' ? '✓' : '↺'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render recommendation detail modal
  const renderRecDetail = (obj) => {
    const contactName = (obj.title || '').replace(/^Рекомендация:\s*/, '');
    const rec = parseRecDescription(obj.description);
    return (
      <div onClick={() => setShowDetail(null)} onTouchStart={e => e.stopPropagation()} onTouchMove={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
        <div ref={modalRef} onClick={e => e.stopPropagation()}
          onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
          style={{
            background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
            width: '100%', maxHeight: '92vh', overflowY: 'auto', animation: 'slideUp 0.3s ease',
            transform: `translate(${dragX}px, ${dragY}px)`,
            transition: dragging ? 'none' : 'transform 0.3s ease',
          }}>

          <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 0', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>
            <button onClick={() => setShowDetail(null)} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>← Назад</button>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, opacity: 0.5 }}></div>
            </div>
            <div style={{ width: 50 }}></div>
          </div>

          <div style={{ padding: '12px 20px calc(40px + var(--tg-safe-bottom, 0px))' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: 'rgba(10,132,255,0.12)', color: '#0A84FF' }}>Рекомендация</span>
              <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{obj.type}</span>
            </div>

            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>{contactName}</h2>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>📍 {obj.city}</div>

            {/* Contact actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {obj.contact && (
                <a href={`tel:${obj.contact.replace(/[\s\-()]/g, '')}`} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: 'var(--accent)', borderRadius: 12, padding: '14px',
                  color: '#0D0D0D', fontSize: 16, fontWeight: 700, textDecoration: 'none',
                }}>📞 Позвонить {obj.contact}</a>
              )}
              {rec.telegram && (
                <a href={`https://t.me/${rec.telegram.replace('@', '')}`} target="_blank" rel="noreferrer" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: '#0088cc', borderRadius: 12, padding: '14px',
                  color: 'white', fontSize: 16, fontWeight: 700, textDecoration: 'none',
                }}>💬 Написать в Telegram {rec.telegram}</a>
              )}
            </div>

            {rec.comment && (
              <div style={{ background: 'var(--bg-primary)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8 }}>КОММЕНТАРИЙ</div>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{rec.comment}</div>
              </div>
            )}

            {/* Who recommended */}
            <div style={{ background: 'var(--bg-primary)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 12 }}>ПОРЕКОМЕНДОВАЛ</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>
                  {obj.author_initials || '??'}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{obj.author_name || 'Аноним'}</div>
                </div>
              </div>
            </div>

            {/* Author actions */}
            {obj.is_author && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button onClick={() => handleToggleStatus(obj)} style={{ flex: 1, background: 'var(--bg-elevated)', color: obj.status === 'active' ? 'var(--success)' : '#0A84FF', border: '1px solid var(--border)', borderRadius: 10, padding: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  {obj.status === 'active' ? '✓ Завершить' : '↺ Открыть'}
                </button>
              </div>
            )}

            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center' }}>
              Опубликовано: {formatDate(obj.created_at)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Header title="Объекты" left={onNavigate && (
        <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>← Назад</button>
      )} right={
        <button onClick={openCreate} style={{ background: 'var(--accent)', color: '#0D0D0D', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ Предложить</button>
      } />

      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Dashboard */}
        <div style={{ display: 'flex', gap: 8, animation: 'fadeInUp 0.3s ease both' }}>
          <div style={{ flex: 1, background: 'var(--bg-card)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>{counts.active}</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 6 }}>активных</span>
          </div>
          <div style={{ flex: 1, background: 'var(--bg-card)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-tertiary)' }}>{counts.closed}</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 6 }}>завершённых</span>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          {TYPES.map(t => (
            <button key={t} onClick={() => setFilter(t)} style={{
              background: filter === t ? 'var(--accent)' : 'var(--bg-card)',
              color: filter === t ? '#0D0D0D' : 'var(--text-secondary)',
              border: '1px solid ' + (filter === t ? 'var(--accent)' : 'var(--border)'),
              borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            }}>{t}</button>
          ))}
        </div>

        {/* Objects */}
        {objects.map((obj, i) => (
          obj.object_type === 'recommendation' ? renderRecCard(obj, i) : renderObjectCard(obj, i)
        ))}

        {objects.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div>Нет объектов</div>
          </div>
        )}
      </div>


      {/* Choice Modal */}
      {showChoiceModal && (
        <div onClick={() => setShowChoiceModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
            padding: '20px 20px 40px', width: '100%', animation: 'slideUp 0.3s ease',
          }}>
            <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, margin: '0 auto 16px' }}></div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>Что вы хотите сделать?</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div onClick={() => { setShowChoiceModal(false); resetForm(); setShowForm(true); }} style={{
                background: 'var(--bg-primary)', borderRadius: 16, padding: 20,
                border: '1px solid var(--border)', cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Разместить объект</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>Подробное описание с фото, бюджетом и деталями</div>
              </div>

              <div onClick={() => { setShowChoiceModal(false); resetRecForm(); setShowRecForm(true); }} style={{
                background: 'var(--bg-primary)', borderRadius: 16, padding: 20,
                border: '1px solid var(--border)', cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>👤</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Порекомендовать контакт</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>Быстро поделиться контактом клиента или работодателя</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (() => {
        const obj = showDetail;
        if (obj.object_type === 'recommendation') return renderRecDetail(obj);
        const badge = statusBadge(obj);
        const hasPhotos = obj.photos?.length > 0;
        return (
          <div onClick={() => setShowDetail(null)} onTouchStart={e => e.stopPropagation()} onTouchMove={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
            <div ref={modalRef} onClick={e => e.stopPropagation()}
              onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
              style={{
                background: 'var(--bg-card)',
                width: '100%', height: '100%', maxHeight: '100vh', overflowY: 'auto',
                animation: 'slideUp 0.3s ease',
                transform: `translate(${dragX}px, ${dragY}px)`,
                transition: dragging ? 'none' : 'transform 0.3s ease',
              }}>

              {/* Top bar: back button + swipe handle */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 0', paddingTop: 'calc(var(--tg-safe-top, 0px) + 12px)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>
                <button onClick={() => setShowDetail(null)} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>← Назад</button>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, opacity: 0.5 }}></div>
                </div>
                <div style={{ width: 50 }}></div>
              </div>

              {/* Photo gallery */}
              {hasPhotos && (
                <div onTouchStart={e => e.stopPropagation()} onTouchMove={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()} style={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', marginTop: 8 }}>
                  {obj.photos.map((p, idx) => (
                    <div key={idx} onClick={() => setLightbox({ photos: obj.photos, index: idx })} style={{ minWidth: '100%', scrollSnapAlign: 'start', background: '#111', display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                      <img src={p} alt="" style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }} />
                    </div>
                  ))}
                </div>
              )}
              {hasPhotos && obj.photos.length > 1 && (
                <div style={{ textAlign: 'center', padding: '6px 0', fontSize: 11, color: 'var(--text-tertiary)' }}>
                  ← свайп для просмотра • {obj.photos.length} фото →
                </div>
              )}

              <div style={{ padding: '12px 20px calc(40px + var(--tg-safe-bottom, 0px))' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: badge.bg, color: badge.color }}>{badge.text}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{obj.type}</span>
                  </div>
                  <span onClick={() => window.open(`https://www.google.com/maps/search/${encodeURIComponent(obj.city + (obj.district ? ' ' + obj.district : '') + ', Moldova')}`)} style={{ fontSize: 13, color: '#0A84FF', cursor: 'pointer', textDecoration: 'underline' }}>📍 {obj.city}{obj.district ? `, ${obj.district}` : ''}</span>
                </div>

                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, lineHeight: 1.3 }}>{obj.title}</h2>

                {obj.description && (
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 16, whiteSpace: 'pre-wrap' }}>{obj.description}</div>
                )}

                {/* Info grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  {(obj.budget > 0 || obj.budget_label) && (
                    <div style={{ background: 'var(--bg-primary)', borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>💰 Бюджет</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>{obj.budget_label || `${obj.budget.toLocaleString()} MDL`}</div>
                    </div>
                  )}
                  {obj.workers_needed > 0 && (
                    <div style={{ background: 'var(--bg-primary)', borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Нужно людей</div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>👷 {obj.workers_needed}</div>
                    </div>
                  )}
                  {obj.start_date && (
                    <div style={{ background: 'var(--bg-primary)', borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Начало работ</div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>🗓 {obj.start_date}</div>
                    </div>
                  )}
                  {obj.urgency === 'urgent' && (
                    <div style={{ background: 'rgba(255,159,10,0.08)', borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Срочность</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#FF9F0A' }}>🔥 Срочно</div>
                    </div>
                  )}
                </div>

                {/* Contacts */}
                <div style={{ background: 'var(--bg-primary)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 12 }}>КОНТАКТЫ</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>
                      {obj.author_initials || '??'}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{obj.author_name || 'Аноним'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Автор объекта</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {obj.contact && (
                      <div style={{ background: 'var(--bg-card)', borderRadius: 10, padding: '10px 14px' }}>
                        <ContactLink value={obj.contact} />
                      </div>
                    )}
                    {obj.author_username && (
                      <a href={`https://t.me/${obj.author_username}`} target="_blank" rel="noreferrer" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        background: '#0088cc', borderRadius: 10, padding: '10px 14px',
                        color: 'white', fontSize: 14, fontWeight: 600, textDecoration: 'none',
                      }}>💬 Написать в Telegram</a>
                    )}
                    {obj.author_telegram_id && !obj.author_username && (
                      <a href={`tg://user?id=${obj.author_telegram_id}`} target="_blank" rel="noreferrer" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        background: '#0088cc', borderRadius: 10, padding: '10px 14px',
                        color: 'white', fontSize: 14, fontWeight: 600, textDecoration: 'none',
                      }}>💬 Написать в Telegram</a>
                    )}
                  </div>
                </div>

                {/* Author actions */}
                {obj.is_author && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <button onClick={() => openEdit(obj)} style={{ flex: 1, background: 'var(--bg-elevated)', color: 'var(--accent)', border: '1px solid var(--border)', borderRadius: 10, padding: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>✎ Изменить</button>
                    <button onClick={() => handleToggleStatus(obj)} style={{ flex: 1, background: 'var(--bg-elevated)', color: obj.status === 'active' ? 'var(--success)' : '#0A84FF', border: '1px solid var(--border)', borderRadius: 10, padding: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                      {obj.status === 'active' ? '✓ Завершить' : '↺ Открыть'}
                    </button>
                  </div>
                )}

                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center' }}>
                  Опубликовано: {formatDate(obj.created_at)}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Create/Edit Object Modal */}
      {showForm && (
        <div onClick={(e) => { if (e.target === e.currentTarget) { setShowForm(false); resetForm(); } }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
            padding: '20px 20px calc(40px + var(--tg-safe-bottom, 0px))', width: '100%', maxHeight: '92vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', animation: 'slideUp 0.3s ease',
          }}>
            <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, margin: '0 auto 12px' }}></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{editingObj ? 'Редактировать объект' : 'Новый объект'}</h3>
              <div onClick={() => { setShowForm(false); resetForm(); }} style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: 'var(--text-secondary)', flexShrink: 0 }}>✕</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div data-error={formErrors.types ? 'true' : undefined}>
                <label style={{ fontSize: 12, color: formErrors.types ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>🏗 Вид работ (можно несколько) <span style={{ color: 'var(--error)' }}>*</span></label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {FORM_TYPES.map(t => {
                    const sel = form.types.includes(t);
                    return (
                      <div key={t} onClick={() => { setForm(f => ({ ...f, types: toggleType(f.types, t) })); setFormErrors(p => ({ ...p, types: false })); if (t === 'Другое' && !sel) {} else if (t === 'Другое' && sel) setCustomType(''); }} style={{
                        padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        background: sel ? 'var(--accent-dim)' : 'var(--bg-primary)',
                        color: sel ? 'var(--accent)' : 'var(--text-secondary)',
                        border: '1px solid ' + (sel ? 'var(--accent)' : formErrors.types ? 'var(--error)' : 'var(--border)'),
                      }}>{sel ? '✓ ' : ''}{t}</div>
                    );
                  })}
                </div>
              </div>
              <div style={{ overflow: 'hidden', maxHeight: form.types.includes('Другое') ? 70 : 0, opacity: form.types.includes('Другое') ? 1 : 0, transition: 'max-height 0.2s ease, opacity 0.2s ease' }}>
                <div>
                  <label style={{ fontSize: 12, color: formErrors.customType ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>✏️ Укажите вид работ <span style={{ color: 'var(--error)' }}>*</span></label>
                  <input value={customType} onChange={e => { setCustomType(e.target.value); setFormErrors(p => ({ ...p, customType: false })); }} placeholder="Например: Штукатурка, Ландшафт..." style={{ ...inputStyle, borderColor: formErrors.customType ? 'var(--error)' : undefined }} />
                </div>
              </div>
              <div>
                <div style={{ position: 'relative' }} data-error={formErrors.city ? 'true' : undefined}>
                  <label style={{ fontSize: 12, color: formErrors.city ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>📍 Город <span style={{ color: 'var(--error)' }}>*</span></label>
                  <input value={form.city} onChange={e => { const v = e.target.value; const plz = findPlz(v); setForm(f => ({ ...f, city: v, ...(plz ? { district: plz } : {}) })); setFormErrors(p => ({ ...p, city: false, ...(plz ? { district: false } : {}) })); setCitySuggestions(suggestCities(v)); }} onBlur={() => setTimeout(() => setCitySuggestions([]), 150)} placeholder="Chisinau, Balti..." style={{ ...inputStyle, borderColor: formErrors.city ? 'var(--error)' : undefined }} />
                  {citySuggestions.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                      background: 'var(--bg-card)', border: '1px solid var(--border)',
                      borderRadius: 10, marginTop: 4, overflow: 'hidden',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    }}>
                      {citySuggestions.map(c => (
                        <div key={c} onPointerDown={(e) => {
                          e.preventDefault();
                          const plz = findPlz(c);
                          setForm(f => ({ ...f, city: c, ...(plz ? { district: plz } : {}) }));
                          setFormErrors(p => ({ ...p, city: false, ...(plz ? { district: false } : {}) }));
                          setCitySuggestions([]);
                        }} style={{
                          padding: '10px 14px', fontSize: 14, color: 'var(--text-primary)',
                          cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          borderBottom: '1px solid var(--border)',
                        }}>
                          <span>{c}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: formErrors.title ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>📝 Название <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={form.title} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, title: v })); setFormErrors(p => ({ ...p, title: false })); }} placeholder="Маляр на объект — квартира 85м²" style={{ ...inputStyle, borderColor: formErrors.title ? 'var(--error)' : undefined }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Описание</label>
                <textarea value={form.description} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, description: v })); }} placeholder="Подробности..." rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>💰 Бюджет</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: budgetMode === 'amount' ? 8 : 0 }}>
                  {[
                    { id: 'amount', label: 'Сумма' },
                    { id: 'discuss', label: 'Обсуждается' },
                  ].map(m => (
                    <div key={m.id} onClick={() => { setBudgetMode(m.id); if (m.id !== 'amount') setForm(f => ({ ...f, budget: '' })); }} style={{
                      flex: 1, textAlign: 'center', padding: '8px 4px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      background: budgetMode === m.id ? 'var(--accent-dim)' : 'var(--bg-primary)',
                      color: budgetMode === m.id ? 'var(--accent)' : 'var(--text-secondary)',
                      border: '1px solid ' + (budgetMode === m.id ? 'var(--accent)' : 'var(--border)'),
                    }}>{m.label}</div>
                  ))}
                </div>
                <div style={{ overflow: 'hidden', maxHeight: budgetMode === 'amount' ? 50 : 0, opacity: budgetMode === 'amount' ? 1 : 0, transition: 'max-height 0.2s ease, opacity 0.2s ease', marginTop: budgetMode === 'amount' ? 8 : 0 }}>
                  <input type="number" inputMode="numeric" value={form.budget} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, budget: v })); }} placeholder="Сумма в MDL" style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>👷 Людей нужно</label>
                <input type="number" inputMode="numeric" value={form.workers_needed} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, workers_needed: v === '' ? '' : Math.max(1, parseInt(v) || 1) })); }} min="1" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>🗓 Начало работ</label>
                <select value={form.start_date} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, start_date: v, start_date_exact: '' })); }} style={inputStyle}>
                  <option value="">Выберите</option>
                  <option value="Срочно">Срочно</option>
                  <option value="Эта неделя">Эта неделя</option>
                  <option value="Следующая неделя">Следующая неделя</option>
                  <option value="Этот месяц">Этот месяц</option>
                  <option value="Гибко">Гибко</option>
                  <option value="Точная дата">Точная дата</option>
                </select>
              </div>
              <div style={{ overflow: 'hidden', maxHeight: form.start_date === 'Точная дата' ? 70 : 0, opacity: form.start_date === 'Точная дата' ? 1 : 0, transition: 'max-height 0.2s ease, opacity 0.2s ease' }}>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>📅 Выберите дату</label>
                  <input type="date" value={form.start_date_exact} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, start_date_exact: v })); }} style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: formErrors.contact ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>📞 Контакт <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={form.contact} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, contact: v })); setFormErrors(p => ({ ...p, contact: false })); }} placeholder="+373 60 123456 или @username" style={{ ...inputStyle, borderColor: formErrors.contact ? 'var(--error)' : undefined }} />
              </div>

              {/* Photo upload */}
              {!editingObj && (
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Фото объекта (до 10)</label>
                  <input ref={fileRef} type="file" accept="image/*" multiple onChange={handlePhotos} style={{ display: 'none' }} />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {previews.map((src, idx) => (
                      <div key={idx} style={{ width: 64, height: 64, borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
                        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div onClick={() => removePhoto(idx)} style={{
                          position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 11, display: 'flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}>✕</div>
                      </div>
                    ))}
                    {photos.length < 10 && (
                      <div onClick={() => { setCitySuggestions([]); document.activeElement?.blur(); setTimeout(() => fileRef.current?.click(), 50); }} style={{
                        width: 64, height: 64, borderRadius: 10, border: '2px dashed var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 24, color: 'var(--text-tertiary)', cursor: 'pointer',
                      }}>📎</div>
                    )}
                  </div>
                </div>
              )}

              <button disabled={submitting} onClick={handleSubmit} style={{
                background: submitting ? 'var(--text-tertiary)' : 'var(--accent)', color: '#0D0D0D', border: 'none', borderRadius: 12,
                padding: '14px', fontSize: 16, fontWeight: 700, cursor: submitting ? 'default' : 'pointer', marginTop: 8,
                opacity: submitting ? 0.6 : 1, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
              }}>{submitting ? 'Отправка...' : (editingObj ? 'Сохранить изменения' : 'Опубликовать')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Recommendation Form Modal */}
      {showRecForm && (
        <div onClick={(e) => { if (e.target === e.currentTarget) { setShowRecForm(false); resetRecForm(); } }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
            padding: '20px 20px 40px', width: '100%', maxHeight: '85vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', animation: 'slideUp 0.3s ease',
          }}>
            <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, margin: '0 auto 12px' }}></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>👤 Порекомендовать контакт</h3>
              <div onClick={() => { setShowRecForm(false); resetRecForm(); }} style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: 'var(--text-secondary)', flexShrink: 0 }}>✕</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: formErrors.recType ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>🏗 Вид работ (можно несколько) <span style={{ color: 'var(--error)' }}>*</span></label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {FORM_TYPES.filter(t => t !== 'Другое').map(t => {
                    const sel = recForm.types.includes(t);
                    return (
                      <div key={t} onClick={() => { setRecForm(f => ({ ...f, types: toggleType(f.types, t) })); setFormErrors(p => ({ ...p, recType: false })); }} style={{
                        padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        background: sel ? 'var(--accent-dim)' : 'var(--bg-primary)',
                        color: sel ? 'var(--accent)' : 'var(--text-secondary)',
                        border: '1px solid ' + (sel ? 'var(--accent)' : formErrors.recType ? 'var(--error)' : 'var(--border)'),
                      }}>{sel ? '✓ ' : ''}{t}</div>
                    );
                  })}
                </div>
              </div>

              <div style={{ position: 'relative' }}>
                <label style={{ fontSize: 12, color: formErrors.recCity ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>📍 Город <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={recForm.city} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, city: v })); setFormErrors(p => ({ ...p, recCity: false })); setRecCitySuggestions(suggestCities(v)); }} onBlur={() => setTimeout(() => setRecCitySuggestions([]), 150)} placeholder="Chisinau, Balti..." style={{ ...inputStyle, borderColor: formErrors.recCity ? 'var(--error)' : undefined }} />
                {recCitySuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: 10, marginTop: 4, overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  }}>
                    {recCitySuggestions.map(c => (
                      <div key={c} onPointerDown={(e) => {
                        e.preventDefault();
                        setRecForm(f => ({ ...f, city: c }));
                        setFormErrors(p => ({ ...p, recCity: false }));
                        setRecCitySuggestions([]);
                      }} style={{
                        padding: '10px 14px', fontSize: 14, color: 'var(--text-primary)',
                        cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        borderBottom: '1px solid var(--border)',
                      }}>
                        <span>{c}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{findPlz(c)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize: 12, color: formErrors.recName ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>👤 Имя контакта <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={recForm.contactName} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, contactName: v })); setFormErrors(p => ({ ...p, recName: false })); }} placeholder="Иван Петров" style={{ ...inputStyle, borderColor: formErrors.recName ? 'var(--error)' : undefined }} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: formErrors.recPhone ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>📞 Телефон <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={recForm.phone} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, phone: v })); setFormErrors(p => ({ ...p, recPhone: false })); }} placeholder="+373 60 123456" type="tel" style={{ ...inputStyle, borderColor: formErrors.recPhone ? 'var(--error)' : undefined }} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>💬 Telegram @username</label>
                <input value={recForm.telegram} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, telegram: v })); }} placeholder="@username" style={inputStyle} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>📝 Краткий комментарий</label>
                <textarea value={recForm.comment} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, comment: v })); }} placeholder="Ищет маляра на квартиру, бюджет ~5000 MDL" rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              <button disabled={submitting} onClick={handleRecSubmit} style={{
                background: submitting ? 'var(--text-tertiary)' : 'var(--accent)', color: '#0D0D0D', border: 'none', borderRadius: 12,
                padding: '14px', fontSize: 16, fontWeight: 700, cursor: submitting ? 'default' : 'pointer', marginTop: 8,
                opacity: submitting ? 0.6 : 1, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
              }}>{submitting ? 'Отправка...' : 'Опубликовать'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.97)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column',
        }}>
          {/* Top bar with close button */}
          <div onClick={() => setLightbox(null)} style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
            padding: '8px 12px',
            paddingTop: 'calc(var(--tg-safe-top, 0px) + 8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div onClick={(e) => { e.stopPropagation(); setLightbox(null); }} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 12,
              background: 'rgba(255,255,255,0.12)',
              cursor: 'pointer', color: '#fff', fontSize: 15, fontWeight: 600,
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
            }}>
              <span style={{ fontSize: 20 }}>‹</span>
              Назад
            </div>
            {lightbox.photos.length > 1 && (
              <div style={{
                padding: '10px 16px', borderRadius: 12,
                background: 'rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.8)', fontSize: 14, fontWeight: 600,
                backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              }}>
                {lightbox.index + 1} / {lightbox.photos.length}
              </div>
            )}
          </div>

          {/* Navigation arrows */}
          {lightbox.photos.length > 1 && lightbox.index > 0 && (
            <div onClick={e => { e.stopPropagation(); setLightbox(lb => ({ ...lb, index: lb.index - 1 })); }} style={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 24, color: '#fff', zIndex: 10,
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
            }}>‹</div>
          )}
          {lightbox.photos.length > 1 && lightbox.index < lightbox.photos.length - 1 && (
            <div onClick={e => { e.stopPropagation(); setLightbox(lb => ({ ...lb, index: lb.index + 1 })); }} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 24, color: '#fff', zIndex: 10,
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
            }}>›</div>
          )}

          <img onClick={e => e.stopPropagation()}
            onTouchStart={e => { lbTouchX.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
              const dx = e.changedTouches[0].clientX - lbTouchX.current;
              if (Math.abs(dx) > 50) {
                if (dx < 0 && lightbox.index < lightbox.photos.length - 1) setLightbox(lb => ({ ...lb, index: lb.index + 1 }));
                if (dx > 0 && lightbox.index > 0) setLightbox(lb => ({ ...lb, index: lb.index - 1 }));
              }
            }}
            src={lightbox.photos[lightbox.index]} alt=""
            style={{ maxWidth: '95vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }} />
        </div>
      )}
    </div>
  );
}
